import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import firebaseApp from "../../firebase/credenciales";

const storage = getStorage(firebaseApp);

/**
 * Servicio de almacenamiento de documentos de compras/facturas
 * Almacena archivos en Firebase Storage para auditoría
 */

/**
 * Sube un archivo de factura a Firebase Storage
 * @param {Object} params - Parámetros de subida
 * @param {string} params.rucEmpresa - RUC de la empresa
 * @param {string} params.proveedorRuc - RUC del proveedor
 * @param {string} params.serie - Serie del comprobante
 * @param {string} params.numero - Número del comprobante
 * @param {File} params.archivo - Archivo a subir
 * @param {string} params.tipoArchivo - "xml" | "pdf" | "imagen"
 * @returns {Promise<string>} URL de descarga del archivo
 */
export const subirArchivoFactura = async ({ rucEmpresa, proveedorRuc, serie, numero, archivo, tipoArchivo }) => {
  if (!archivo) throw new Error("No se proporcionó archivo para subir");
  
  const timestamp = Date.now();
  const extension = archivo.name.split(".").pop().toLowerCase();
  const nombreArchivo = `${serie}-${numero}_${timestamp}.${extension}`;
  
  // Estructura: compras/{rucEmpresa}/facturas/{proveedorRuc}/{tipoArchivo}/{nombreArchivo}
  const rutaStorage = `compras/${rucEmpresa}/facturas/${proveedorRuc}/${tipoArchivo}/${nombreArchivo}`;
  
  const storageRef = ref(storage, rutaStorage);
  
  // Determinar content type
  let contentType = "application/octet-stream";
  if (extension === "xml") contentType = "application/xml";
  else if (extension === "pdf") contentType = "application/pdf";
  else if (["jpg", "jpeg"].includes(extension)) contentType = "image/jpeg";
  else if (extension === "png") contentType = "image/png";
  
  const metadata = {
    contentType,
    customMetadata: {
      rucEmpresa,
      proveedorRuc,
      serie,
      numero,
      tipoArchivo,
      fechaSubida: new Date().toISOString()
    }
  };
  
  await uploadBytes(storageRef, archivo, metadata);
  const downloadURL = await getDownloadURL(storageRef);
  
  console.log(`[FacturaStorage] Archivo subido: ${rutaStorage}`);
  return downloadURL;
};

/**
 * Sube múltiples archivos de una factura (XML y/o PDF)
 * @param {Object} params - Parámetros
 * @param {string} params.rucEmpresa - RUC de la empresa
 * @param {string} params.proveedorRuc - RUC del proveedor  
 * @param {string} params.serie - Serie del comprobante
 * @param {string} params.numero - Número del comprobante
 * @param {File} [params.archivoXml] - Archivo XML (opcional)
 * @param {File} [params.archivoPdf] - Archivo PDF/imagen (opcional)
 * @returns {Promise<{xmlUrl: string|null, pdfUrl: string|null}>}
 */
export const subirArchivosFactura = async ({ rucEmpresa, proveedorRuc, serie, numero, archivoXml, archivoPdf }) => {
  const resultado = { xmlUrl: null, pdfUrl: null };
  
  const promesas = [];
  
  if (archivoXml) {
    promesas.push(
      subirArchivoFactura({
        rucEmpresa,
        proveedorRuc,
        serie,
        numero,
        archivo: archivoXml,
        tipoArchivo: "xml"
      }).then(url => { resultado.xmlUrl = url; })
    );
  }
  
  if (archivoPdf) {
    const ext = archivoPdf.name.split(".").pop().toLowerCase();
    const tipo = ["jpg", "jpeg", "png"].includes(ext) ? "imagen" : "pdf";
    
    promesas.push(
      subirArchivoFactura({
        rucEmpresa,
        proveedorRuc,
        serie,
        numero,
        archivo: archivoPdf,
        tipoArchivo: tipo
      }).then(url => { resultado.pdfUrl = url; })
    );
  }
  
  await Promise.all(promesas);
  
  return resultado;
};

export default {
  subirArchivoFactura,
  subirArchivosFactura
};
